import React, { memo, useEffect, useState } from 'react';

import { App, Button, Modal, Typography } from 'antd';

import { useMutation } from '@tanstack/react-query';
import apiService from 'v2/services/api';

import { Icon } from '@eduzz/ui-antd-theme';

import {
  MODAL_NEWS_RVP_BANNER_SRC,
  MODAL_NEWS_RVP_ENDPOINTS,
  MODAL_NEWS_RVP_SESSION_KEY,
  MODAL_NEWS_RVP_TEXTS
} from './constants';
import { RvpBenefitsList } from './RvpBenefitsList';

const MODAL_WIDTH = 528;

export interface ModalNewsRVPProps {
  available?: boolean;
  disabled?: boolean;
}

export const ModalNewsRVP = memo(({ available, disabled }: ModalNewsRVPProps) => {
  const { message } = App.useApp();
  const [opened, setOpened] = useState(false);

  const disabledInThisSession = !!sessionStorage.getItem(MODAL_NEWS_RVP_SESSION_KEY);

  const activate = useMutation({
    mutationFn: () => apiService.put(MODAL_NEWS_RVP_ENDPOINTS.activate, {}).toPromise(),
    onSuccess: () => {
      message.success(MODAL_NEWS_RVP_TEXTS.activateSuccess);
      setOpened(false);
    },
    onError: () => {
      message.error(MODAL_NEWS_RVP_TEXTS.activateError);
    }
  });

  const deny = useMutation({
    mutationFn: () => apiService.put(MODAL_NEWS_RVP_ENDPOINTS.deny, {}).toPromise(),
    onSuccess: () => {
      message.success(MODAL_NEWS_RVP_TEXTS.denySuccess);
      setOpened(false);
    },
    onError: () => {
      message.error(MODAL_NEWS_RVP_TEXTS.denyError);
    }
  });

  const isPending = activate.isPending || deny.isPending;

  const handleClose = () => {
    sessionStorage.setItem(MODAL_NEWS_RVP_SESSION_KEY, 'true');
    setOpened(false);
  };

  useEffect(() => {
    setOpened(true);
  }, []);

  return (
    <Modal
      open={opened && available && !disabled && !disabledInThisSession}
      onCancel={handleClose}
      maskClosable={false}
      footer={null}
      width={MODAL_WIDTH}
      closeIcon={
        <Icon.X size={20} className='text-(--uizz-antd-color-white)' aria-label={MODAL_NEWS_RVP_TEXTS.closeLabel} />
      }
      classNames={{ content: 'overflow-hidden p-0', body: 'p-0' }}
    >
      <img src={MODAL_NEWS_RVP_BANNER_SRC} alt='' aria-hidden className='block h-auto w-full' />

      <div className='flex flex-col gap-4 p-6'>
        <div className='flex flex-col gap-1'>
          <Typography.Title level={4} className='m-0'>
            {MODAL_NEWS_RVP_TEXTS.title}
          </Typography.Title>
          <Typography.Text>{MODAL_NEWS_RVP_TEXTS.subtitle}</Typography.Text>
        </div>

        <RvpBenefitsList benefits={MODAL_NEWS_RVP_TEXTS.benefits} />

        <Typography.Paragraph className='mb-0'>
          <Typography.Text strong>{MODAL_NEWS_RVP_TEXTS.callToAction}</Typography.Text>{' '}
          {MODAL_NEWS_RVP_TEXTS.autoActivationNotice}
        </Typography.Paragraph>

        <div className='flex flex-wrap items-center justify-end gap-2 max-sm:flex-col-reverse'>
          <Button
            color='default'
            variant='filled'
            className='max-sm:w-full'
            onClick={() => deny.mutate()}
            loading={deny.isPending}
            disabled={isPending}
          >
            {MODAL_NEWS_RVP_TEXTS.denyButton}
          </Button>
          <Button
            type='primary'
            className='max-sm:w-full'
            onClick={() => activate.mutate()}
            loading={activate.isPending}
            disabled={isPending}
          >
            {MODAL_NEWS_RVP_TEXTS.activateButton}
          </Button>
        </div>
      </div>
    </Modal>
  );
});
